import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { FieldRow } from '../FieldRow';
import { ProfileFormProps } from './ProfileForm.types';

/**
 * Profile form component displaying station data in a two-column layout
 * Renders all station fields using FieldRow components with appropriate input types
 *
 * Features:
 * - Two-column responsive layout
 * - Field change tracking with visual indicators
 * - Edit mode restrictions based on station type
 * - Different field types (text, select, date)
 * - Status filtering for competitor stations
 * - URL validation for photo and Google Maps links
 * - GPS coordinate input with number validation
 *
 * @param props - Component props containing station data and form configuration
 * @returns JSX element containing the complete station profile form
 */
export const ProfileForm = memo(
  ({
    stationData,
    isEditMode,
    isOkkoStation,
    changedFields,
    validationErrors,
    regions,
    provinces,
    divisions,
    brands,
    statuses,
    onFieldChange,
    translateStatus,
    formatDate
  }: ProfileFormProps): JSX.Element => {
    if (!stationData) {
      return <></>;
    }

    /**
     * Checks if a specific field has been changed from its original value
     * @param field - Field name to check
     * @returns True if field has been changed, false otherwise
     */
    const isFieldChanged = (field: string) => changedFields.has(field);
    const hasError = (field: string) => validationErrors?.has(field) ?? false;

    /**
     * Determines if a field can be edited based on current mode and station type
     * @param field - Field name to check
     * @returns True if field is editable, false otherwise
     */
    const isFieldEditable = (field: string): boolean => {
      if (!isEditMode) return false;
      if (field === 'unicode') return false;
      if (isOkkoStation) {
        return field === 'photo_url' || field === 'google_maps_url';
      }
      return true;
    };

    const competitorStatuses = useMemo(
      () => statuses.filter(s => s.value === 'active' || s.value === 'closed'),
      [statuses]
    );

    return (
      <Box className="station-profile-form-grid">
        <Box className={`station-profile-form-column ${isEditMode ? 'edit' : ''}`}>
          <FieldRow
            label="Юнікод"
            field="unicode"
            value={stationData.unicode}
            isFieldChanged={isFieldChanged}
            hasError={hasError('unicode')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Бренд"
            field="brand"
            value={stationData.brand}
            type="select"
            options={brands}
            isFieldChanged={isFieldChanged}
            hasError={hasError('brand')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Адреса"
            field="address"
            value={stationData.address}
            isFieldChanged={isFieldChanged}
            hasError={hasError('address')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Адреса (примітка)"
            field="address_note"
            value={stationData.address_note}
            isFieldChanged={isFieldChanged}
            hasError={hasError('address_note')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Статус АЗК"
            field="status"
            value={stationData.status}
            type="select"
            options={isOkkoStation ? statuses : competitorStatuses}
            isFieldChanged={isFieldChanged}
            hasError={hasError('status')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Додатковий код"
            field="additional_code"
            value={stationData.additional_code}
            isFieldChanged={isFieldChanged}
            hasError={hasError('additional_code')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Дата початку роботи"
            field="start_date"
            value={stationData.start_date}
            type="date"
            isFieldChanged={isFieldChanged}
            hasError={hasError('start_date')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />
        </Box>

        <Box className={`station-profile-form-column ${isEditMode ? 'edit' : ''}`}>
          <FieldRow
            label="Регіональне відділення"
            field="regional_office"
            value={stationData.regional_office}
            type="select"
            options={regions}
            isFieldChanged={isFieldChanged}
            hasError={hasError('regional_office')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Область"
            field="province"
            value={stationData.province}
            type="select"
            options={provinces}
            isFieldChanged={isFieldChanged}
            hasError={hasError('province')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Дивізіон"
            field="division"
            value={stationData.division}
            type="select"
            options={divisions}
            isFieldChanged={isFieldChanged}
            hasError={hasError('division')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Номер АЗК"
            field="station_number"
            value={stationData.station_number}
            isFieldChanged={isFieldChanged}
            hasError={hasError('station_number')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Фото"
            field="photo_url"
            value={stationData.photo_url}
            isFieldChanged={isFieldChanged}
            hasError={hasError('photo_url')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="Посилання на Google map"
            field="google_maps_url"
            value={stationData.google_maps_url}
            isFieldChanged={isFieldChanged}
            hasError={hasError('google_maps_url')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="GPS Широта"
            field="latitude"
            value={stationData.latitude}
            isFieldChanged={isFieldChanged}
            hasError={hasError('latitude')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />

          <FieldRow
            label="GPS Довгота"
            field="longitude"
            value={stationData.longitude}
            isFieldChanged={isFieldChanged}
            hasError={hasError('longitude')}
            isOkkoStation={isOkkoStation}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            translateStatus={translateStatus}
            formatDate={formatDate}
          />
        </Box>
      </Box>
    );
  }
);

ProfileForm.displayName = 'ProfileForm';
